// Browser-only glue: reads the live platform surface into the injectable RuntimeEnvironment
// and builds the network-probe context. The probe logic itself is pure and lives in probes/.

import type {
  ApiNodeInfo,
  BlockerProbeContext,
  DeviceProbeContext,
  EntropyProbeContext,
  FetchLike,
  FingerprintProbeContext,
  IpDetails,
  IpProbeContext,
  IspProbeContext,
  MediaProbeContext,
  NetProbeContext,
  NodeProbeContext,
  PermissionsProbeContext,
  ProbeCapabilities,
  ReachabilityProbeContext,
  RtcProbeContext,
  RuntimeEnvironment,
  StorageProbeContext,
} from './types';

/** Default public STUN server for WebRTC reachability (tenants configure their own TURN later). */
const DEFAULT_STUN = 'stun:stun.l.google.com:19302';

interface ExtendedNavigator extends Navigator {
  globalPrivacyControl?: boolean;
  deviceMemory?: number;
  connection?: { effectiveType?: string; downlink?: number; rtt?: number };
  userAgentData?: {
    platform: string;
    mobile: boolean;
    brands: { brand: string; version: string }[];
  };
}

function readCanvas2d(): boolean {
  try {
    return !!document.createElement('canvas').getContext('2d');
  } catch {
    return false;
  }
}

function readAudioVideo(): boolean {
  try {
    const video = document.createElement('video');
    return typeof video.canPlayType === 'function';
  } catch {
    return false;
  }
}

// First-party cookies: navigator.cookieEnabled can report true even when cookies are blocked,
// so actually set a test cookie and read it back. Falls back to the flag if document.cookie throws.
function readCookiesEnabled(): boolean {
  try {
    document.cookie = 'bp_ck_probe=1; SameSite=Lax; Path=/; Max-Age=60';
    const ok = document.cookie.includes('bp_ck_probe=1');
    document.cookie = 'bp_ck_probe=; SameSite=Lax; Path=/; Max-Age=0';
    return ok;
  } catch {
    return navigator.cookieEnabled === true;
  }
}

// Modern-CSS baseline + which feature(s) are missing (for the failure note / support triage).
function readCssBaseline(): { modern: boolean; missing: string[] } {
  try {
    const missing: string[] = [];
    if (!CSS.supports('display', 'grid')) missing.push('grid');
    if (!CSS.supports('--x', '0')) missing.push('custom properties');
    if (!CSS.supports('selector(:has(*))')) missing.push(':has()');
    return { modern: missing.length === 0, missing };
  } catch {
    return { modern: false, missing: [] };
  }
}

function readLocalStorage(): boolean {
  try {
    const probe = '__bp_ls__';
    localStorage.setItem(probe, '1');
    localStorage.removeItem(probe);
    return typeof sessionStorage === 'object';
  } catch {
    return false;
  }
}

function readGraphics(): { webgl2: boolean; renderer?: string; vendor?: string } {
  try {
    const gl = document.createElement('canvas').getContext('webgl2');
    if (!gl) {
      return { webgl2: false };
    }
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    const renderer = debugInfo
      ? (gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) as string)
      : undefined;
    const vendor = debugInfo
      ? (gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) as string)
      : undefined;
    return { webgl2: true, renderer, vendor };
  } catch {
    return { webgl2: false };
  }
}

/** Widest supported color gamut, via the `(color-gamut: …)` media query. */
function readColorGamut(): string | undefined {
  try {
    if (!window.matchMedia) {
      return undefined;
    }
    if (window.matchMedia('(color-gamut: rec2020)').matches) {
      return 'rec2020';
    }
    if (window.matchMedia('(color-gamut: p3)').matches) {
      return 'p3';
    }
    return 'srgb';
  } catch {
    return undefined;
  }
}

/** Snapshots the live browser environment. Permissionless: reads no API that prompts the user. */
export function readBrowserEnvironment(): RuntimeEnvironment {
  const nav = navigator as ExtendedNavigator;

  const connection = nav.connection
    ? {
        effectiveType: nav.connection.effectiveType,
        downlinkMbps: nav.connection.downlink,
        rttMs: nav.connection.rtt,
      }
    : undefined;

  const userAgentData = nav.userAgentData
    ? {
        platform: nav.userAgentData.platform,
        mobile: nav.userAgentData.mobile,
        brands: nav.userAgentData.brands,
      }
    : undefined;

  let timeZone: string | undefined;
  try {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    timeZone = undefined;
  }

  const cssBaseline = readCssBaseline();

  const workersMissing: string[] = [];
  if (typeof Worker === 'undefined') workersMissing.push('web workers');
  if (!('serviceWorker' in nav)) workersMissing.push('service workers');

  return {
    userAgent: nav.userAgent,
    secureContext: window.isSecureContext,
    cookieEnabled: readCookiesEnabled(),
    globalPrivacyControl: nav.globalPrivacyControl,
    languages: nav.languages ?? [nav.language],
    timeZone,
    screen: { width: screen.width, height: screen.height, pixelRatio: window.devicePixelRatio },
    viewport: { width: window.innerWidth, height: window.innerHeight },
    hardware: { cores: nav.hardwareConcurrency, memoryGb: nav.deviceMemory },
    graphics: readGraphics(),
    canvas2d: readCanvas2d(),
    webdriver: nav.webdriver === true,
    audioVideo: readAudioVideo(),
    webAuthn: 'PublicKeyCredential' in window,
    // screen.isExtended (Window Management API) — undefined where unsupported, so the probe blocks.
    multiDisplay: (screen as Screen & { isExtended?: boolean }).isExtended,
    fullscreen: document.fullscreenEnabled,
    maxTouchPoints: nav.maxTouchPoints,
    finePointer: window.matchMedia?.('(pointer: fine)').matches ?? false,
    cssModern: cssBaseline.modern,
    cssMissing: cssBaseline.missing,
    localStorage: readLocalStorage(),
    webAssembly: typeof WebAssembly === 'object' && typeof WebAssembly.instantiate === 'function',
    webGpu: 'gpu' in nav,
    workers: workersMissing.length === 0,
    workersMissing,
    doNotTrack: nav.doNotTrack === '1' || nav.doNotTrack === 'yes' || (window as Window & { doNotTrack?: string }).doNotTrack === '1',
    offscreenCanvas: typeof OffscreenCanvas !== 'undefined',
    sharedArrayBuffer: typeof SharedArrayBuffer !== 'undefined',
    indexedDb: 'indexedDB' in window && window.indexedDB != null,
    hdr: window.matchMedia?.('(dynamic-range: high)').matches ?? false,
    colorGamut: readColorGamut(),
    pictureInPicture: (document as Document & { pictureInPictureEnabled?: boolean }).pictureInPictureEnabled ?? false,
    connection,
    userAgentData,
  };
}

/**
 * Latency-races the configured API nodes (network.closest-node, PRD §11.3): pings each node's
 * /ping and picks the lowest RTT. Permissionless. With one node it trivially selects it; with
 * none configured `selected` is null and the caller falls back to the same-origin /diag.
 */
export async function selectClosestNode(
  nodes: readonly ApiNodeInfo[],
  deps: { fetch?: FetchLike; now?: () => number; timeoutMs?: number; retryDelayMs?: number } = {},
): Promise<NodeProbeContext> {
  const doFetch = deps.fetch ?? ((url, init) => fetch(url, init));
  const now = deps.now ?? (() => performance.now());
  const timeoutMs = deps.timeoutMs ?? 3000;
  const retryDelayMs = deps.retryDelayMs ?? 300;

  const race = () =>
    Promise.all(
      nodes.map(async (node) => {
        const base = node.endpoint.replace(/\/$/, '');
        const controller = typeof AbortController !== 'undefined' ? new AbortController() : undefined;
        const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : undefined;
        const start = now();
        try {
          const response = await doFetch(`${base}/ping`, { method: 'GET', signal: controller?.signal });
          if (timer) {
            clearTimeout(timer);
          }
          return response.ok
            ? { region: node.region, endpoint: base, latencyMs: Math.round(now() - start) }
            : { region: node.region, endpoint: base, latencyMs: null };
        } catch {
          if (timer) {
            clearTimeout(timer);
          }
          return { region: node.region, endpoint: base, latencyMs: null };
        }
      }),
    );

  const pickReachable = (candidates: Awaited<ReturnType<typeof race>>) => {
    const reachable = candidates.filter(
      (c): c is { region: string; endpoint: string; latencyMs: number } => c.latencyMs !== null,
    );
    reachable.sort((a, b) => a.latencyMs - b.latencyMs);
    return reachable;
  };

  if (nodes.length === 0) {
    return { selected: null, candidates: [] };
  }

  let candidates = await race();
  let reachable = pickReachable(candidates);
  if (reachable.length === 0) {
    // One transient ping blip must not silently demote the whole scan to the same-origin
    // fallback (worse isolation, no node attribution): pause briefly and re-race once.
    // Worst case this adds retryDelayMs + timeoutMs before falling back — only when every
    // node is genuinely unreachable.
    await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
    candidates = await race();
    reachable = pickReachable(candidates);
  }

  return { selected: reachable[0] ?? null, candidates };
}

/** Builds the network-probe context against an API node base URL. */
// 5 MB download default: large enough for TCP slow-start to ramp so fast links aren't
// understated, well under the server's 8 MB clamp. The download probe warms the connection first.
export function createNetContext(baseUrl: string, downloadBytes = 5_000_000, uploadBytes = 3_000_000): NetProbeContext {
  const trimmed = baseUrl.replace(/\/$/, '');
  return {
    fetch: (url, init) => fetch(url, init),
    now: () => performance.now(),
    pingUrl: `${trimmed}/ping`,
    downloadUrl: `${trimmed}/download?bytes=${downloadBytes}`,
    downloadBytes,
    uploadUrl: `${trimmed}/upload`,
    uploadBytes,
    serverTimeUrl: `${trimmed}/time`,
    wallClockNow: () => Date.now(),
    // Stream-timed raw download: the clock starts at the FIRST received chunk (excluding its
    // bytes) and stops at the last, so TTFB and response packaging are outside the window.
    downloadTimed: async (bytes, signal) => {
      try {
        const response = await fetch(`${trimmed}/download?bytes=${bytes}&raw=true`, { method: 'GET', signal });
        if (!response.ok || !response.body) {
          return null;
        }
        const reader = response.body.getReader();
        let counted = 0;
        let startTs = -1;
        let endTs = -1;
        for (;;) {
          const { done, value } = await reader.read();
          if (done) {
            break;
          }
          const t = performance.now();
          if (startTs < 0) {
            startTs = t; // first chunk marks time zero; its bytes rode the untimed TTFB
          } else {
            counted += value?.length ?? 0;
            endTs = t;
          }
        }
        if (startTs < 0) {
          return null; // no body at all
        }
        if (endTs <= startTs || counted === 0) {
          // Arrived in a single chunk — the transfer was too fast to stream-time at this
          // size. elapsedMs 0 tells the caller "faster than measurable", distinct from null
          // ("streaming unavailable", which falls back to the legacy measurement).
          return { receivedBytes: bytes, elapsedMs: 0 };
        }
        return { receivedBytes: counted, elapsedMs: endTs - startTs };
      } catch {
        return null;
      }
    },
  };
}

/**
 * Wraps the permissioned browser APIs. Each call prompts the user, so these are only invoked
 * when the engine config opts into permissioned probes (PRD §6.2).
 */
export function createBrowserCapabilities(): ProbeCapabilities {
  return {
    requestCameraMic: async () => {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      const video = stream.getVideoTracks().length > 0;
      const audio = stream.getAudioTracks().length > 0;
      stream.getTracks().forEach((track) => track.stop());
      return { video, audio };
    },
    requestPreciseLocation: () =>
      new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
          (position) => resolve({ granted: true, accuracyMeters: position.coords.accuracy }),
          () => resolve({ granted: false }),
          { enableHighAccuracy: true, timeout: 10_000 },
        );
      }),
  };
}

/**
 * WebRTC reachability via ICE gathering against a STUN server (network.webrtc). Permissionless
 * — no prompt. Resolves once gathering completes or after a short timeout; a server-reflexive
 * (srflx) candidate means the STUN server was reached through NAT.
 */
export function createRtcContext(stunUrls: readonly string[] = [DEFAULT_STUN]): RtcProbeContext {
  return {
    gather: () =>
      new Promise((resolve) => {
        const PeerConnection = window.RTCPeerConnection;
        if (!PeerConnection) {
          resolve({ reachedStun: false, candidateTypes: [] });
          return;
        }
        const types = new Set<string>();
        let settled = false;
        const pc = new PeerConnection({ iceServers: [{ urls: [...stunUrls] }] });
        const finish = () => {
          if (settled) {
            return;
          }
          settled = true;
          try {
            pc.close();
          } catch {
            // ignore
          }
          resolve({ reachedStun: types.has('srflx') || types.has('relay'), candidateTypes: [...types] });
        };
        const timer = setTimeout(finish, 5_000);
        pc.onicecandidate = (event) => {
          if (!event.candidate) {
            clearTimeout(timer);
            finish();
            return;
          }
          const type = event.candidate.type ?? /(?: typ )(\w+)/.exec(event.candidate.candidate)?.[1];
          if (type) {
            types.add(type);
          }
        };
        pc.createDataChannel('bp');
        pc.createOffer()
          .then((offer) => pc.setLocalDescription(offer))
          .catch(() => {
            clearTimeout(timer);
            finish();
          });
      }),
  };
}

const DEFAULT_INTERNET_URL = 'https://www.gstatic.com/generate_204';
const DEFAULT_WS_URL = 'wss://echo.websocket.events';

/** A fetch reaches its target when it resolves at all (even an opaque/no-cors or error status). */
async function reachFetch(target: string, url: string, init: RequestInit, timeoutMs: number) {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : undefined;
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : undefined;
  try {
    await fetch(url, { ...init, signal: controller?.signal });
    return { target, reachable: true };
  } catch {
    return { target, reachable: false };
  } finally {
    if (timer) {
      clearTimeout(timer);
    }
  }
}

/** A WebSocket reaches its target when it opens before the timeout. */
function reachWebSocket(target: string, url: string, timeoutMs: number): Promise<{ target: string; reachable: boolean }> {
  return new Promise((resolve) => {
    if (typeof WebSocket === 'undefined') {
      resolve({ target, reachable: false });
      return;
    }
    let settled = false;
    const finish = (reachable: boolean, socket?: WebSocket) => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timer);
      try {
        socket?.close();
      } catch {
        // ignore
      }
      resolve({ target, reachable });
    };
    const timer = setTimeout(() => finish(false), timeoutMs);
    try {
      const socket = new WebSocket(url);
      socket.onopen = () => finish(true, socket);
      socket.onerror = () => finish(false, socket);
    } catch {
      finish(false);
    }
  });
}

/**
 * Endpoint-reachability matrix (network.reachability, PRD §7). Probes the target classes a
 * browser can test on App Service PaaS — HTTPS (our node + the public internet), WebSocket, and
 * WebRTC/STUN. Arbitrary ports can't be reached from a browser, so the matrix is 443/WS/WebRTC.
 */
export function createReachabilityContext(
  options: { nodeEndpoint?: string; internetUrl?: string; wsUrl?: string; stunUrls?: readonly string[]; timeoutMs?: number } = {},
): ReachabilityProbeContext {
  const internetUrl = options.internetUrl ?? DEFAULT_INTERNET_URL;
  const wsUrl = options.wsUrl ?? DEFAULT_WS_URL;
  const stunUrls = options.stunUrls ?? [DEFAULT_STUN];
  const timeoutMs = options.timeoutMs ?? 4000;

  return {
    check: async () => {
      const results: { target: string; reachable: boolean }[] = [];

      if (options.nodeEndpoint) {
        results.push(await reachFetch('https-node', `${options.nodeEndpoint.replace(/\/$/, '')}/ping`, {}, timeoutMs));
      }
      // Opaque cross-origin GET — resolves if the request reached the server, regardless of CORS.
      results.push(await reachFetch('https-internet', internetUrl, { mode: 'no-cors', cache: 'no-store' }, timeoutMs));
      results.push(await reachWebSocket('websocket', wsUrl, timeoutMs));
      // A server-reflexive STUN candidate means UDP/WebRTC reached out through NAT.
      const stun = await createRtcContext(stunUrls).gather();
      results.push({ target: 'webrtc-stun', reachable: stun.reachedStun });

      return results;
    },
  };
}

const ENTROPY_SALT = 'bp-entropy-v1';

/**
 * Coarse, salted, truncated bucket of an attribute value (12 bits → ≤4096 buckets). Truncation +
 * salting makes the token a k-anonymity bucket, not a reversible fingerprint — so even what's
 * sent to the server is already low-entropy (PRD §14 self-awareness).
 */
function entropyBucket(attribute: string, value: string): string {
  let hash = 2166136261;
  const input = `${ENTROPY_SALT}|${attribute}|${value}`;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return ((hash >>> 20) & 0xfff).toString(16); // 12 bits, 3 hex chars
}

/** Builds the coarse per-attribute buckets from the environment snapshot (no raw values sent). */
function collectEntropyBuckets(env: RuntimeEnvironment): { attribute: string; token: string }[] {
  const out: { attribute: string; token: string }[] = [];
  const push = (attribute: string, value: string | undefined | null) => {
    if (value) {
      out.push({ attribute, token: entropyBucket(attribute, value) });
    }
  };
  push('ua', env.userAgentData ? env.userAgentData.brands.map((b) => b.brand).join(',') : env.userAgent);
  push('platform', env.userAgentData?.platform);
  push('lang', env.languages[0]);
  push('tz', env.timeZone);
  push('screen', env.screen ? `${env.screen.width}x${env.screen.height}@${env.screen.pixelRatio}` : undefined);
  push('hw', env.hardware ? `${env.hardware.cores ?? '?'}/${env.hardware.memoryGb ?? '?'}` : undefined);
  push('gpu', env.graphics?.renderer);
  return out;
}

/**
 * Fingerprint-entropy port (privacy.fingerprint-entropy). Computes coarse per-attribute buckets
 * from the environment, POSTs them to the server's population model, and returns the rarity. The
 * raw fingerprint never leaves the browser; only k-anonymity buckets are sent.
 */
export function createEntropyContext(
  postUrl: string,
  env: RuntimeEnvironment,
  deps: { fetch?: FetchLike } = {},
): EntropyProbeContext {
  const doFetch = deps.fetch ?? ((url, init) => fetch(url, init));
  return {
    measure: async () => {
      const attributes = collectEntropyBuckets(env);
      if (attributes.length === 0) {
        return null;
      }
      try {
        const response = await doFetch(postUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes }),
        });
        if (!response.ok) {
          return null;
        }
        const body = (await response.json()) as { bits?: number; oneInN?: number };
        if (typeof body.bits !== 'number') {
          return null;
        }
        return { bits: body.bits, oneInN: typeof body.oneInN === 'number' ? body.oneInN : Math.round(2 ** body.bits) };
      } catch {
        return null;
      }
    },
  };
}

/**
 * ISP port (network.isp). GETs the server's same-origin /diag/isp, which resolves the caller's
 * own address against a local ASN database — the IP never leaves our infrastructure, and only
 * the coarse network identity (organization + ASN) comes back. Any failure is null (the probe
 * reports Blocked).
 */
export function createIspContext(url: string, deps: { fetch?: FetchLike } = {}): IspProbeContext {
  const doFetch = deps.fetch ?? ((u, init) => fetch(u, init));
  return {
    lookup: async () => {
      try {
        const response = await doFetch(url, { method: 'GET' });
        if (!response.ok) {
          return null;
        }
        const body = (await response.json()) as { isp?: string | null; asn?: number | null };
        return { organization: body.isp ?? null, asn: typeof body.asn === 'number' ? body.asn : null };
      } catch {
        return null;
      }
    },
  };
}

/**
 * Public-IP port (network.public-ip / network.ip-details). GETs the server's same-origin
 * /diag/ip (cheap, Free probe) and /diag/ip-details (adds reverse DNS, source port and
 * coarse location, Business probe). The server resolves the caller's own address locally —
 * it never leaves our infrastructure — and masks it at ingestion before anything is stored.
 * Any failure is null (the probe reports Blocked).
 */
export function createIpContext(
  addressUrl: string,
  detailsUrl: string,
  deps: { fetch?: FetchLike } = {},
): IpProbeContext {
  const doFetch = deps.fetch ?? ((u, init) => fetch(u, init));
  const get = async <T>(url: string): Promise<T | null> => {
    try {
      const response = await doFetch(url, { method: 'GET' });
      return response.ok ? ((await response.json()) as T) : null;
    } catch {
      return null;
    }
  };
  return {
    address: async () => {
      const body = await get<{ ip?: string | null; version?: string | null }>(addressUrl);
      return body ? { ip: body.ip ?? null, version: body.version ?? null } : null;
    },
    details: async () => {
      const body = await get<Partial<IpDetails>>(detailsUrl);
      if (!body) {
        return null;
      }
      return {
        ip: body.ip ?? null,
        version: body.version ?? null,
        port: typeof body.port === 'number' ? body.port : null,
        reverseDns: body.reverseDns ?? null,
        city: body.city ?? null,
        region: body.region ?? null,
        country: body.country ?? null,
      };
    },
  };
}

/** Media decode-capability via the MediaCapabilities API (media.codecs). Permissionless. */
export function createMediaContext(): MediaProbeContext {
  return {
    canDecode: async (contentType) => {
      const mc = navigator.mediaCapabilities;
      if (!mc?.decodingInfo) {
        return false;
      }
      const result = await mc.decodingInfo({
        type: 'file',
        video: { contentType, width: 1920, height: 1080, bitrate: 4_000_000, framerate: 30 },
      });
      return result.supported;
    },
    drmSupport: async (keySystem) => {
      if (!navigator.requestMediaKeySystemAccess) {
        return false;
      }
      const config: MediaKeySystemConfiguration[] = [
        {
          initDataTypes: ['cenc'],
          videoCapabilities: [{ contentType: 'video/mp4; codecs="avc1.42E01E"' }],
        },
      ];
      try {
        await navigator.requestMediaKeySystemAccess(keySystem, config);
        return true;
      } catch {
        return false; // not supported / rejected
      }
    },
  };
}

/**
 * Third-party-cookie posture (storage.third-party-cookies). A fully accurate check needs a
 * cross-origin endpoint; this client-only heuristic asserts "blocked" only when the browser
 * refuses a SameSite=None cookie outright, and returns null (indeterminate) otherwise.
 */
export function createStorageContext(): StorageProbeContext {
  return {
    thirdPartyCookiesBlocked: async () => {
      try {
        document.cookie = 'bp_3p_probe=1; SameSite=None; Secure; Path=/; Max-Age=60';
        const present = document.cookie.includes('bp_3p_probe=1');
        document.cookie = 'bp_3p_probe=; SameSite=None; Secure; Path=/; Max-Age=0';
        return present ? null : true;
      } catch {
        return null;
      }
    },
    // StorageManager.estimate() reports the origin's quota + current usage (storage.indexeddb).
    estimate: async () => {
      try {
        if (!navigator.storage?.estimate) {
          return null;
        }
        const { quota, usage } = await navigator.storage.estimate();
        if (typeof quota !== 'number') {
          return null;
        }
        return { quotaBytes: quota, usageBytes: typeof usage === 'number' ? usage : 0 };
      } catch {
        return null;
      }
    },
  };
}

/**
 * Async device-hardware reads (device.cpu-architecture). Permissionless: CPU architecture comes
 * from UA-CH high-entropy hints (Chromium only; null elsewhere).
 */
export function createDeviceContext(): DeviceProbeContext {
  return {
    cpuArchitecture: async () => {
      try {
        const uaData = (navigator as ExtendedNavigator & {
          getHighEntropyValues?: (hints: string[]) => Promise<{ architecture?: string; bitness?: string }>;
        }).userAgentData as
          | { getHighEntropyValues?: (hints: string[]) => Promise<{ architecture?: string; bitness?: string }> }
          | undefined;
        if (!uaData?.getHighEntropyValues) {
          return null;
        }
        const hints = await uaData.getHighEntropyValues(['architecture', 'bitness']);
        if (!hints.architecture) {
          return null;
        }
        return hints.bitness ? `${hints.architecture} (${hints.bitness}-bit)` : hints.architecture;
      } catch {
        return null;
      }
    },
  };
}

/** Permissions API snapshot (privacy.permissions). query() never prompts — it only reads state. */
export function createPermissionsContext(): PermissionsProbeContext {
  return {
    query: async (name) => {
      if (!navigator.permissions?.query) {
        return null;
      }
      try {
        const status = await navigator.permissions.query({ name: name as PermissionName });
        return status.state;
      } catch {
        return null; // unsupported permission name on this browser
      }
    },
  };
}

/**
 * Fingerprint-surface analysis (privacy.fingerprint-hashes). PRD §16 self-aware: we compute
 * canvas/WebGL/audio fingerprints twice and report only whether each surface is 'stable'
 * (identical across reads — fingerprintable), 'randomized' (differs — anti-fingerprinting), or
 * 'blocked'. The raw fingerprints never leave this function. (Per-session farbling can read as
 * 'stable' within one page load — a known limitation.)
 */
export function createFingerprintContext(): FingerprintProbeContext {
  const hash = (s: string): number => {
    let h = 0;
    for (let i = 0; i < s.length; i++) {
      h = (h * 31 + s.charCodeAt(i)) | 0;
    }
    return h;
  };

  const canvasFp = (): string => {
    const c = document.createElement('canvas');
    c.width = 200;
    c.height = 50;
    const g = c.getContext('2d');
    if (!g) {
      throw new Error('no 2d');
    }
    g.textBaseline = 'top';
    g.font = '14px Arial';
    g.fillStyle = '#069';
    g.fillText('BrowserProbe \u{1F512} fp', 2, 2);
    g.strokeStyle = 'rgba(0,80,120,0.6)';
    g.beginPath();
    g.arc(50, 25, 18, 0, Math.PI * 2);
    g.stroke();
    return c.toDataURL();
  };

  const webglFp = (): string => {
    const c = document.createElement('canvas');
    const gl = (c.getContext('webgl') ?? c.getContext('experimental-webgl')) as WebGLRenderingContext | null;
    if (!gl) {
      throw new Error('no webgl');
    }
    const dbg = gl.getExtension('WEBGL_debug_renderer_info');
    const vendor = dbg ? gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL) : '';
    const renderer = dbg ? gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) : '';
    return `${vendor}|${renderer}|${gl.getParameter(gl.MAX_TEXTURE_SIZE)}`;
  };

  const audioFp = async (): Promise<string> => {
    const Ctx = (window.OfflineAudioContext ??
      (window as Window & { webkitOfflineAudioContext?: typeof OfflineAudioContext }).webkitOfflineAudioContext);
    if (!Ctx) {
      throw new Error('no audio');
    }
    const ctx = new Ctx(1, 5000, 44100);
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = 10000;
    const comp = ctx.createDynamicsCompressor();
    osc.connect(comp);
    comp.connect(ctx.destination);
    osc.start(0);
    const rendered = await ctx.startRendering();
    const data = rendered.getChannelData(0);
    let sum = 0;
    for (let i = 0; i < data.length; i++) {
      sum += Math.abs(data[i]);
    }
    return sum.toString();
  };

  const verdict = (a: string, b: string): 'stable' | 'randomized' => (a === b ? 'stable' : 'randomized');

  return {
    analyze: async () => {
      const surfaces: { surface: string; state: 'stable' | 'randomized' | 'blocked' }[] = [];

      try {
        surfaces.push({ surface: 'canvas', state: verdict(String(hash(canvasFp())), String(hash(canvasFp()))) });
      } catch {
        surfaces.push({ surface: 'canvas', state: 'blocked' });
      }
      try {
        surfaces.push({ surface: 'webgl', state: verdict(webglFp(), webglFp()) });
      } catch {
        surfaces.push({ surface: 'webgl', state: 'blocked' });
      }
      try {
        const [a, b] = await Promise.all([audioFp(), audioFp()]);
        surfaces.push({ surface: 'audio', state: verdict(a, b) });
      } catch {
        surfaces.push({ surface: 'audio', state: 'blocked' });
      }

      return surfaces;
    },
  };
}

/**
 * Ad/tracker-blocker detection (privacy.tracker-blocker, privacy.tracking-pixel). Loads a
 * first-party bait resource whose URL matches common filter-list patterns; onerror (or a
 * timeout) means a blocker prevented the load. Cache-busted so each run re-evaluates.
 */
export function createBlockerContext(): BlockerProbeContext {
  return {
    blocked: (url, kind) =>
      new Promise((resolve) => {
        const bait = `${url}?_=${Date.now()}`;
        let done = false;
        const finish = (wasBlocked: boolean, el?: HTMLElement) => {
          if (done) {
            return;
          }
          done = true;
          if (el?.parentNode) {
            el.parentNode.removeChild(el);
          }
          resolve(wasBlocked);
        };
        const timer = setTimeout(() => finish(true), 4000);
        if (kind === 'image') {
          const img = new Image();
          img.onload = () => {
            clearTimeout(timer);
            finish(false);
          };
          img.onerror = () => {
            clearTimeout(timer);
            finish(true);
          };
          img.src = bait;
        } else {
          const script = document.createElement('script');
          script.async = true;
          script.onload = () => {
            clearTimeout(timer);
            finish(false, script);
          };
          script.onerror = () => {
            clearTimeout(timer);
            finish(true, script);
          };
          script.src = bait;
          document.head.appendChild(script);
        }
      }),
  };
}
